import { ChevronDown } from 'lucide-react'
import { useId, useState, type ReactNode } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { cn } from '@/lib/utils'
import {
  CHANGED_PRESETS,
  formatRange,
  formatShortDate,
  isRealDay,
  NO_CONTACT_PRESETS,
  pastRange,
  RECENT_CONTACT_PRESETS,
  type DatePreset,
} from '../query/dateRanges'
import type { Filter, OptionDef, PropertyDef, Register } from '../query/types'
import { keepScrolling } from './keepScrolling'
import { OptionChecklist } from './OptionChecklist'
import {
  changedChoice,
  lastContactChoice,
  listValues,
  PERIOD_PRESETS,
  periodChoice,
  quickFor,
  setChanged,
  setLastContact,
  setListFilter,
  setPeriod,
  setTextFilter,
  textValue,
  type ChangedPreset,
  type LastContactChoice,
  type PeriodPreset,
  type QuietDays,
  type RecentContact,
} from './quickConditions'
import { RefPicker } from './RefPicker'
import type { FilterOptions, RefKind } from './useFilterOptions'

/** The quick filter on `last_activity_at` goes by this name; the register calls the property "Laatste activiteit". */
const CHANGED_LABEL = 'Gewijzigd'
const CONTACT_LABEL = 'Laatste contact'
/** A row with a radio or checkbox: 44 px high on a phone. */
const ROW = 'flex min-h-8 cursor-pointer items-center gap-2 rounded-md px-2 py-1 text-sm hover:bg-muted max-md:min-h-11'
const INPUT = 'max-md:h-11 max-md:text-base'
const SELECT =
  'h-8 rounded-lg border border-border bg-background px-2 text-sm disabled:opacity-50 max-md:h-11 max-md:text-base'

/** The name of an id on a button or chip: "Ik" and "Geen eigenaar" are fixed, the rest comes from the choices or the names. */
function chosenName(property: PropertyDef, id: string, options: FilterOptions): string {
  if (property.type === 'user' && id === 'me') return 'Ik'
  if (property.type === 'user' && id === 'none') return property.key === 'owner' ? 'Geen eigenaar' : 'Niemand'
  return options.optionsFor(property)?.find((option) => option.value === id)?.label ?? options.names.get(id) ?? 'Onbekend'
}

/** "Eigenaar" when unset, "Eigenaar: Ik" for one value, "Categorie (2)" for more. */
function listText(label: string, names: string[]): string {
  if (names.length === 0) return label
  return names.length === 1 ? `${label}: ${names[0]}` : `${label} (${names.length})`
}

/** The days a preset covers, as shown next to it ("3–16 okt"). */
function presetRange(preset: DatePreset, property: string, today: string): string | null {
  const range = pastRange({ property, ...preset.condition }, today)
  return range && 'from' in range ? formatRange(range, today) : null
}

/**
 * The button of a quick filter and the popover it opens. The mobile sheet shows these too, and a
 * popover from inside a sheet needs its wheel and touch events kept away from the sheet's scroll
 * lock (`keepScrolling`), or the lists in it cannot be scrolled: the options of Categorie, Labels
 * and Eigenaar, and the hits of Organisatie.
 */
function QuickPopover({ label, text, active, children }: { label: string; text: string; active: boolean; children: ReactNode }) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button type="button" variant="outline" size="sm" className={cn(active && 'border-primary text-primary')}>
          {text}
          <ChevronDown aria-hidden />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" aria-label={label} className="w-80 p-3" onWheel={keepScrolling} onTouchMove={keepScrolling}>
        {children}
      </PopoverContent>
    </Popover>
  )
}

// ---- Gewijzigd -------------------------------------------------------------------------------

/**
 * The body of "Gewijzigd" (addendum A.5.2): what the filter works on, the periods with the days
 * they cover, "Aangepast…" with two dates, and who did it. `users` are the team to pick from.
 */
export function ChangedOptions({
  filter,
  today,
  help,
  users,
  onChange,
}: {
  filter: Filter
  today: string
  help: string | null
  users: readonly OptionDef[]
  onChange: (next: Filter) => void
}) {
  const group = useId()
  const byId = useId()
  const choice = changedChoice(filter)
  const hasPeriod = choice.preset !== undefined || choice.range !== undefined
  // "Aangepast…" is picked as soon as it is clicked; it only reaches the filter with two good dates.
  const [customPicked, setCustomPicked] = useState(false)
  const [from, setFrom] = useState(choice.range?.from ?? '')
  const [to, setTo] = useState(choice.range?.to ?? '')
  const custom = customPicked || choice.range !== undefined
  const people = users.filter((user) => user.value !== 'none')

  /** The pick and the two dates are the draft of a period of your own: they go when that period does. */
  function forgetDraft() {
    setCustomPicked(false)
    setFrom('')
    setTo('')
  }
  // The filter lost its period while this panel stayed on screen (the sheet's "Wissen", a chip taken away): start over too.
  const [hadPeriod, setHadPeriod] = useState(hasPeriod)
  if (hasPeriod !== hadPeriod) {
    setHadPeriod(hasPeriod)
    if (!hasPeriod) forgetDraft()
  }

  function setRange(nextFrom: string, nextTo: string) {
    setFrom(nextFrom)
    setTo(nextTo)
    if (isRealDay(nextFrom) && isRealDay(nextTo) && nextFrom <= nextTo) {
      onChange(setChanged(filter, { range: { from: nextFrom, to: nextTo }, by: choice.by }))
    }
  }

  return (
    <div className="flex flex-col gap-3">
      {help ? <p className="text-sm text-muted-foreground">{help}</p> : null}
      <div role="radiogroup" aria-label="Periode" className="flex flex-col">
        {CHANGED_PRESETS.map((preset) => {
          const range = presetRange(preset, 'last_activity_at', today)
          return (
            <label key={preset.key} className={ROW}>
              <input
                type="radio"
                name={group}
                className="size-4 shrink-0 accent-primary"
                checked={!custom && choice.preset === preset.key}
                onChange={() => {
                  forgetDraft()
                  onChange(setChanged(filter, { preset: preset.key as ChangedPreset, by: choice.by }))
                }}
              />
              <span>{preset.label}</span>
              {range ? <span className="ml-auto text-muted-foreground">{range}</span> : null}
            </label>
          )
        })}
        <label className={ROW}>
          <input type="radio" name={group} className="size-4 shrink-0 accent-primary" checked={custom} onChange={() => setCustomPicked(true)} />
          <span>Aangepast…</span>
        </label>
        {custom ? (
          <div className="flex items-center gap-2 pb-1 pl-8">
            <Input type="date" aria-label="Gewijzigd van" value={from} onChange={(event) => setRange(event.target.value, to)} className={INPUT} />
            <span className="text-sm text-muted-foreground">t/m</span>
            <Input type="date" aria-label="Gewijzigd tot en met" value={to} onChange={(event) => setRange(from, event.target.value)} className={INPUT} />
          </div>
        ) : null}
      </div>
      <div className="flex items-center gap-2 border-t border-border pt-3">
        <label htmlFor={byId} className="text-sm">
          Door
        </label>
        <select
          id={byId}
          value={choice.by ?? ''}
          disabled={!hasPeriod}
          title={hasPeriod ? undefined : 'Kies eerst een periode'}
          onChange={(event) => onChange(setChanged(filter, { ...choice, by: event.target.value || null }))}
          className={cn(SELECT, 'min-w-0 flex-1')}
        >
          <option value="">Iedereen</option>
          {people.map((user) => (
            <option key={user.value} value={user.value}>
              {user.value === 'me' ? 'Ik' : user.label}
            </option>
          ))}
          {choice.by && !people.some((user) => user.value === choice.by) ? <option value={choice.by}>Onbekend</option> : null}
        </select>
      </div>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="self-start"
        onClick={() => {
          forgetDraft()
          onChange(setChanged(filter, { by: null }))
        }}
      >
        Wissen
      </Button>
    </div>
  )
}

function ChangedQuick({
  property,
  filter,
  today,
  options,
  users,
  onChange,
}: {
  property: PropertyDef
  filter: Filter
  today: string
  options: FilterOptions
  users: readonly OptionDef[]
  onChange: (next: Filter) => void
}) {
  const choice = changedChoice(filter)
  const preset = CHANGED_PRESETS.find((candidate) => candidate.key === choice.preset)
  let period = ''
  if (preset) period = preset.short
  else if (choice.range) period = formatRange(choice.range, today)
  else if (quickFor(filter, 'last_activity_at').length > 0) period = 'aangepast'
  const by = choice.by ? (choice.by === 'me' ? 'Ik' : (options.names.get(choice.by) ?? 'Onbekend')) : null
  const text = period ? `${CHANGED_LABEL}: ${period}${by ? ` · ${by}` : ''}` : CHANGED_LABEL
  return (
    <QuickPopover label={CHANGED_LABEL} text={text} active={period !== ''}>
      <ChangedOptions filter={filter} today={today} help={property.help} users={users} onChange={onChange} />
    </QuickPopover>
  )
}

// ---- Laatste contact -------------------------------------------------------------------------

/**
 * The body of "Laatste contact" (addendum A.5.2, A.6): contact within a recent span, and the
 * reverse, "Geen contact sinds" a number of days or a date, which by default also finds the
 * relations that never had contact (ED-64).
 */
export function LastContactOptions({
  filter,
  today,
  help,
  onChange,
}: {
  filter: Filter
  today: string
  help: string | null
  onChange: (next: Filter) => void
}) {
  const group = useId()
  const choice = lastContactChoice(filter)
  // Contact within a span (a preset or a period of your own), or the reverse: no contact for a while, or since a date.
  const inSpan = choice.recent !== undefined || choice.range !== undefined
  const reverse = choice.quietDays !== undefined || choice.since !== undefined
  // "Aangepast…" and "Sinds datum…" are picked as soon as they are clicked, before there is a good date.
  const [picked, setPicked] = useState<'range' | 'since' | null>(null)
  const [from, setFrom] = useState(choice.range?.from ?? '')
  const [to, setTo] = useState(choice.range?.to ?? '')
  const [since, setSince] = useState(choice.since ?? '')
  const mode = picked ?? (choice.range ? 'range' : choice.since !== undefined ? 'since' : null)

  /** The pick and the dates are the draft of "Aangepast…" and "Sinds datum…": they go when those choices do. */
  function forgetDraft() {
    setPicked(null)
    setFrom('')
    setTo('')
    setSince('')
  }
  // The filter lost its choice while this panel stayed on screen (the sheet's "Wissen", a chip taken away): start over too.
  const hasChoice = inSpan || reverse
  const [hadChoice, setHadChoice] = useState(hasChoice)
  if (hasChoice !== hadChoice) {
    setHadChoice(hasChoice)
    if (!hasChoice) forgetDraft()
  }

  const write = (next: LastContactChoice) => {
    forgetDraft()
    onChange(setLastContact(filter, next))
  }

  function setRange(nextFrom: string, nextTo: string) {
    setFrom(nextFrom)
    setTo(nextTo)
    if (isRealDay(nextFrom) && isRealDay(nextTo) && nextFrom <= nextTo) {
      onChange(setLastContact(filter, { range: { from: nextFrom, to: nextTo }, includeNever: choice.includeNever }))
    }
  }

  function setSinceDay(day: string) {
    setSince(day)
    if (isRealDay(day)) onChange(setLastContact(filter, { since: day, includeNever: choice.includeNever }))
  }

  return (
    <div className="flex flex-col gap-3">
      {help ? <p className="text-sm text-muted-foreground">{help}</p> : null}
      <fieldset className="flex flex-col">
        <legend className="px-2 pb-1 text-xs font-semibold tracking-wide text-muted-foreground uppercase">Recent</legend>
        {RECENT_CONTACT_PRESETS.map((preset) => {
          const range = presetRange(preset, 'last_contact_at', today)
          return (
            <label key={preset.key} className={ROW}>
              <input
                type="radio"
                name={group}
                className="size-4 shrink-0 accent-primary"
                checked={mode === null && choice.recent === preset.key}
                onChange={() => write({ recent: preset.key as RecentContact, includeNever: choice.includeNever })}
              />
              <span>{preset.label}</span>
              {range ? <span className="ml-auto text-muted-foreground">{range}</span> : null}
            </label>
          )
        })}
        <label className={ROW}>
          <input type="radio" name={group} className="size-4 shrink-0 accent-primary" checked={mode === 'range'} onChange={() => setPicked('range')} />
          <span>Aangepast…</span>
        </label>
        {mode === 'range' ? (
          <div className="flex items-center gap-2 pb-1 pl-8">
            <Input type="date" aria-label="Laatste contact van" value={from} onChange={(event) => setRange(event.target.value, to)} className={INPUT} />
            <span className="text-sm text-muted-foreground">t/m</span>
            <Input type="date" aria-label="Laatste contact tot en met" value={to} onChange={(event) => setRange(from, event.target.value)} className={INPUT} />
          </div>
        ) : null}
      </fieldset>
      <fieldset className="flex flex-col">
        <legend className="px-2 pb-1 text-xs font-semibold tracking-wide text-muted-foreground uppercase">Geen contact sinds</legend>
        <div className="grid grid-cols-3">
          {NO_CONTACT_PRESETS.map((preset) => (
            <label key={preset.key} className={ROW}>
              <input
                type="radio"
                name={group}
                className="size-4 shrink-0 accent-primary"
                checked={mode === null && choice.quietDays === preset.condition.value}
                onChange={() => write({ quietDays: preset.condition.value as QuietDays, includeNever: choice.includeNever })}
              />
              <span>{preset.label}</span>
            </label>
          ))}
        </div>
        <label className={ROW}>
          <input type="radio" name={group} className="size-4 shrink-0 accent-primary" checked={mode === 'since'} onChange={() => setPicked('since')} />
          <span>Sinds datum…</span>
        </label>
        {mode === 'since' ? (
          <div className="pb-1 pl-8">
            <Input type="date" aria-label="Geen contact sinds datum" value={since} onChange={(event) => setSinceDay(event.target.value)} className={INPUT} />
          </div>
        ) : null}
        <label className={ROW}>
          <input
            type="checkbox"
            className="size-4 shrink-0 accent-primary"
            // Nobody without contact has had contact within a span: the box is off there, as well as greyed out.
            checked={choice.includeNever && !inSpan}
            disabled={!reverse}
            onChange={(event) => onChange(setLastContact(filter, { ...choice, includeNever: event.target.checked }))}
          />
          <span>Ook relaties met nog geen contact</span>
        </label>
      </fieldset>
      <Button type="button" variant="ghost" size="sm" className="self-start" onClick={() => write({ includeNever: true })}>
        Wissen
      </Button>
    </div>
  )
}

function LastContactQuick({ property, filter, today, onChange }: { property: PropertyDef; filter: Filter; today: string; onChange: (next: Filter) => void }) {
  const choice = lastContactChoice(filter)
  let summary = ''
  const recent = RECENT_CONTACT_PRESETS.find((preset) => preset.key === choice.recent)
  const quiet = NO_CONTACT_PRESETS.find((preset) => preset.condition.value === choice.quietDays)
  if (recent) summary = recent.short
  else if (choice.range) summary = formatRange(choice.range, today)
  else if (quiet) summary = quiet.short
  else if (choice.since !== undefined) summary = `geen contact sinds ${formatShortDate(choice.since, today)}`
  else if (quickFor(filter, 'last_contact_at').length > 0) summary = 'aangepast'
  return (
    <QuickPopover label={CONTACT_LABEL} text={summary ? `${CONTACT_LABEL}: ${summary}` : CONTACT_LABEL} active={summary !== ''}>
      <LastContactOptions filter={filter} today={today} help={property.help} onChange={onChange} />
    </QuickPopover>
  )
}

// ---- Periode (Lijsten) -----------------------------------------------------------------------

const PERIOD_LABEL = 'Periode'

/** "Periode" of the lists overview: the date of a list in the next 30 days, this month or quarter, past, or between two days. */
function PeriodQuick({ filter, today, onChange }: { filter: Filter; today: string; onChange: (next: Filter) => void }) {
  const group = useId()
  const choice = periodChoice(filter)
  const [customPicked, setCustomPicked] = useState(false)
  const [from, setFrom] = useState(choice.range?.from ?? '')
  const [to, setTo] = useState(choice.range?.to ?? '')
  const custom = customPicked || choice.range !== undefined
  const preset = PERIOD_PRESETS.find((candidate) => candidate.key === choice.preset)
  let text = PERIOD_LABEL
  if (preset) text = `${PERIOD_LABEL}: ${preset.short}`
  else if (choice.range) text = `${PERIOD_LABEL}: ${formatRange(choice.range, today)}`

  function setRange(nextFrom: string, nextTo: string) {
    setFrom(nextFrom)
    setTo(nextTo)
    if (isRealDay(nextFrom) && isRealDay(nextTo) && nextFrom <= nextTo) onChange(setPeriod(filter, { range: { from: nextFrom, to: nextTo } }, today))
  }

  return (
    <QuickPopover label={PERIOD_LABEL} text={text} active={preset !== undefined || choice.range !== undefined}>
      <div className="flex flex-col gap-2">
        <div role="radiogroup" aria-label={PERIOD_LABEL} className="flex flex-col">
          {PERIOD_PRESETS.map((option) => (
            <label key={option.key} className={ROW}>
              <input
                type="radio"
                name={group}
                className="size-4 shrink-0 accent-primary"
                checked={!custom && choice.preset === option.key}
                onChange={() => {
                  setCustomPicked(false)
                  onChange(setPeriod(filter, { preset: option.key as PeriodPreset }, today))
                }}
              />
              <span>{option.label}</span>
            </label>
          ))}
          <label className={ROW}>
            <input type="radio" name={group} className="size-4 shrink-0 accent-primary" checked={custom} onChange={() => setCustomPicked(true)} />
            <span>Aangepast…</span>
          </label>
          {custom ? (
            <div className="flex items-center gap-2 pb-1 pl-8">
              <Input type="date" aria-label="Periode van" value={from} onChange={(event) => setRange(event.target.value, to)} className={INPUT} />
              <span className="text-sm text-muted-foreground">t/m</span>
              <Input type="date" aria-label="Periode tot en met" value={to} onChange={(event) => setRange(from, event.target.value)} className={INPUT} />
            </div>
          ) : null}
        </div>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="self-start"
          onClick={() => {
            setCustomPicked(false)
            setFrom('')
            setTo('')
            onChange(setPeriod(filter, {}, today))
          }}
        >
          Wissen
        </Button>
      </div>
    </QuickPopover>
  )
}

// ---- The list, organisation and text filters -------------------------------------------------

function ListQuick({
  property,
  operator,
  filter,
  options,
  onChange,
}: {
  property: PropertyDef
  operator: string
  filter: Filter
  options: FilterOptions
  onChange: (next: Filter) => void
}) {
  const values = listValues(filter, property.key, operator)
  return (
    <QuickPopover
      label={property.label}
      text={listText(property.label, values.map((id) => chosenName(property, id, options)))}
      active={values.length > 0}
    >
      <OptionChecklist
        label={property.label}
        options={options.optionsFor(property)}
        value={values}
        onChange={(next) => onChange(setListFilter(filter, property.key, operator, next))}
        displayLabel={property.key === 'owner' ? (option) => (option.value === 'none' ? 'Geen eigenaar' : option.label) : undefined}
      />
    </QuickPopover>
  )
}

function RefQuick({
  property,
  operator,
  filter,
  options,
  onChange,
}: {
  property: PropertyDef
  operator: string
  filter: Filter
  options: FilterOptions
  onChange: (next: Filter) => void
}) {
  const values = listValues(filter, property.key, operator)
  const kind: RefKind = property.options?.source === 'persons' ? 'persons' : 'organisations'
  return (
    <QuickPopover
      label={property.label}
      text={listText(property.label, values.map((id) => chosenName(property, id, options)))}
      active={values.length > 0}
    >
      <RefPicker
        kind={kind}
        label={property.label}
        value={values}
        options={options}
        onChange={(next) => onChange(setListFilter(filter, property.key, operator, next))}
      />
    </QuickPopover>
  )
}

/** The text field of a text quick filter; it lives in the popover, so it starts from the filter every time it opens. */
function TextForm({ property, operator, filter, onChange }: { property: PropertyDef; operator: string; filter: Filter; onChange: (next: Filter) => void }) {
  const current = textValue(filter, property.key, operator)
  const [draft, setDraft] = useState(current)
  return (
    <form
      className="flex flex-col gap-2"
      onSubmit={(event) => {
        event.preventDefault()
        onChange(setTextFilter(filter, property.key, operator, draft))
      }}
    >
      <Input aria-label={property.label} maxLength={200} value={draft} onChange={(event) => setDraft(event.target.value)} className={INPUT} />
      <div className="flex items-center gap-2">
        <Button type="submit" size="sm">
          Toepassen
        </Button>
        {current ? (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => {
              setDraft('')
              onChange(setTextFilter(filter, property.key, operator, ''))
            }}
          >
            Wissen
          </Button>
        ) : null}
      </div>
    </form>
  )
}

function TextQuick({ property, operator, filter, onChange }: { property: PropertyDef; operator: string; filter: Filter; onChange: (next: Filter) => void }) {
  const current = textValue(filter, property.key, operator)
  return (
    <QuickPopover label={property.label} text={current ? `${property.label}: ${current}` : property.label} active={current !== ''}>
      <TextForm property={property} operator={operator} filter={filter} onChange={onChange} />
    </QuickPopover>
  )
}

// ---- All of them -----------------------------------------------------------------------------

interface QuickFiltersProps {
  register: Register
  filter: Filter
  options: FilterOptions
  /** Today in Amsterdam as YYYY-MM-DD: the days a period covers are worked out from it. */
  today: string
  onChange: (next: Filter) => void
  /** Show only these quick filters (by key). */
  only?: readonly string[]
  /** Leave these out; the mobile sheet shows "Gewijzigd" and "Laatste contact" apart from the rest. */
  except?: readonly string[]
  /** For the row of buttons: `contents` lets them wrap along with whatever sits beside them. */
  className?: string
}

/**
 * One popover button per quick filter of the register (design §5.2, addendum A.4.3): Eigenaar,
 * Categorie, Labels, Organisatie or Plaats, Laatste contact and Gewijzigd. A control only reads
 * and writes its own conditions in the AND part of the filter; the groups are not its business.
 * The button says what is set: "Eigenaar: Ik", "Categorie (2)", "Gewijzigd: deze week".
 */
export function QuickFilters({ register, filter, options, today, onChange, only, except, className }: QuickFiltersProps) {
  const keys = register.quick_filters.filter((key) => (!only || only.includes(key)) && !except?.includes(key))
  const author = register.properties.find((property) => property.key === 'last_activity_by')
  const users = (author && options.optionsFor(author)) ?? []
  return (
    <div className={cn('flex flex-wrap items-center gap-2', className)}>
      {keys.map((key) => {
        const property = register.properties.find((candidate) => candidate.key === key)
        if (!property) return null
        const shared = { property, filter, onChange }
        switch (key) {
          case 'last_activity_at':
            return <ChangedQuick key={key} {...shared} today={today} options={options} users={users} />
          case 'last_contact_at':
            return <LastContactQuick key={key} {...shared} today={today} />
          case 'city':
            return <TextQuick key={key} {...shared} operator="contains" />
          case 'organisation':
            return <RefQuick key={key} {...shared} operator="contains_any_of" options={options} />
          case 'labels':
            return <ListQuick key={key} {...shared} operator="contains_any_of" options={options} />
          case 'starts_on':
            return <PeriodQuick key={key} filter={filter} today={today} onChange={onChange} />
          case 'owner':
          case 'category':
          case 'kind':
          case 'type':
            return <ListQuick key={key} {...shared} operator="is_any_of" options={options} />
          default:
            return null
        }
      })}
    </div>
  )
}
